import { useState } from 'react';
import { ledger, type LedgerEntry } from '../lib/api';
import { useResource } from '../hooks/useResource';
import { formatDateTime } from '../lib/format';
import { Alert, Badge, Card, Empty, Spinner } from '../components/ui';

/**
 * The statement: every balance movement with its running balance — the audit
 * trail behind the numbers on the overview. Credits render green, debits red.
 */

const TYPE_LABELS: Record<string, string> = {
  deposit: 'Crypto deposit',
  offramp_funding: 'Offramp funding',
  onramp_debit: 'Onramp delivery',
  transfer_out: 'Crypto sent',
  collection: 'NGN received',
  payout: 'Payout',
  payout_reversal: 'Payout reversal',
  adjustment: 'Adjustment',
};

export function Ledger() {
  const [page, setPage] = useState(1);
  const [currency, setCurrency] = useState<'' | 'USDC' | 'NGN'>('');

  const entries = useResource(() => ledger.list(page, currency || undefined), [page, currency]);
  const pagination = entries.data?.pagination;

  function amountCell(entry: LedgerEntry) {
    const negative = entry.amount.startsWith('-');
    const symbol = entry.currency === 'NGN' ? '₦' : '';
    const suffix = entry.currency === 'USDC' ? ' USDC' : '';
    return (
      <span style={{ color: negative ? 'var(--danger)' : 'var(--success)', fontWeight: 600 }}>
        {negative ? '' : '+'}
        {symbol}
        {entry.amount}
        {suffix}
      </span>
    );
  }

  return (
    <>
      <div className="page-header">
        <h1>Ledger</h1>
        <p>Every balance movement, newest first — the statement behind your balances.</p>
      </div>

      {entries.error && <Alert>{entries.error}</Alert>}

      <Card
        title={pagination ? `${pagination.total} entries` : 'Entries'}
        actions={
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <select
              value={currency}
              onChange={(event) => {
                setPage(1);
                setCurrency(event.target.value as '' | 'USDC' | 'NGN');
              }}
              style={{ width: 'auto' }}
            >
              <option value="">All currencies</option>
              <option value="USDC">USDC</option>
              <option value="NGN">NGN</option>
            </select>
            <button className="btn btn-sm" onClick={() => setPage((p) => p - 1)} disabled={page <= 1}>
              Previous
            </button>
            <span className="muted">
              {page} / {pagination?.total_pages ?? 1}
            </span>
            <button
              className="btn btn-sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={!pagination?.has_more}
            >
              Next
            </button>
          </div>
        }
      >
        {entries.loading ? (
          <Spinner />
        ) : !entries.data?.items.length ? (
          <Empty>No movements yet — fund a balance to see your first entry.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Balance after</th>
                  <th>Reference</th>
                </tr>
              </thead>
              <tbody>
                {entries.data.items.map((entry) => (
                  <tr key={entry.id}>
                    <td className="muted">{formatDateTime(entry.created_at)}</td>
                    <td>
                      <Badge tone={entry.currency === 'USDC' ? 'accent' : 'neutral'}>
                        {entry.currency}
                      </Badge>{' '}
                      {TYPE_LABELS[entry.type] ?? entry.type}
                    </td>
                    <td className="mono">{amountCell(entry)}</td>
                    <td className="mono">
                      {entry.currency === 'NGN' ? '₦' : ''}
                      {entry.balance_after}
                      {entry.currency === 'USDC' ? ' USDC' : ''}
                    </td>
                    <td className="mono muted">{entry.reference?.slice(0, 16) ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
